import {
	BOT_COMMAND_PATTERN,
	EMPTY_TRIGGERS,
	EVENT_CODE_PATTERN,
	type FlowTriggers,
	flowTriggersSchema,
	isEmptyTriggers,
	KEYWORD_MATCHES,
	type KeywordMatch,
	MAX_TRIGGER_COMMANDS,
	MAX_TRIGGER_EVENTS,
	MAX_TRIGGER_KEYWORDS,
	parseTriggers,
} from "@velizhanin-dev/bot-scenario"
import { Plus, Trash2 } from "lucide-react"
import { useEffect, useState } from "react"
import type { DraftFlow } from "@/entities/bot-builder"
import { Button } from "@/shared/ui/button"
import { Checkbox } from "@/shared/ui/checkbox"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/shared/ui/dialog"
import { Input } from "@/shared/ui/input"
import { Label } from "@/shared/ui/label"
import { NativeSelect } from "@/shared/ui/native-select"

const MATCH_LABELS: Record<KeywordMatch, string> = {
	exact: "точно совпадает",
	contains: "содержит",
}

interface FlowTriggersDialogProps {
	flow: DraftFlow | null
	onOpenChange: (open: boolean) => void
	onSave: (flowId: string, triggers: FlowTriggers | null) => Promise<unknown>
}

/** Команды, ключевые слова и события, запускающие поток, и режим «в фоне». */
export function FlowTriggersDialog({ flow, onOpenChange, onSave }: FlowTriggersDialogProps) {
	const [value, setValue] = useState<FlowTriggers>(EMPTY_TRIGGERS)
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		if (flow) setValue(parseTriggers(flow.triggers))
	}, [flow])

	const parsed = flowTriggersSchema.safeParse(value)
	const error = parsed.success ? null : (parsed.error.issues[0]?.message ?? "Проверьте поля")
	const setCommands = (commands: FlowTriggers["commands"]) => setValue({ ...value, commands })
	const setKeywords = (keywords: FlowTriggers["keywords"]) => setValue({ ...value, keywords })
	const setEvents = (events: FlowTriggers["events"]) => setValue({ ...value, events })

	const save = async () => {
		if (!flow || !parsed.success) return
		setSaving(true)
		await onSave(flow.id, isEmptyTriggers(parsed.data) ? null : parsed.data)
		setSaving(false)
		onOpenChange(false)
	}

	return (
		<Dialog open={!!flow} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>Запуск воронки «{flow?.name}»</DialogTitle>
					<DialogDescription>
						Команда или слово запускают воронку из любого места диалога, даже посреди вопроса. Слово
						«содержит» не перебивает ответ на вопрос.
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-2">
					<p className="text-sm font-medium">Команды</p>
					{value.commands.map((item, index) => (
						<div key={index} className="grid grid-cols-[8rem_1fr_auto] items-center gap-1">
							<div className="flex items-center rounded-md border border-input px-2">
								<span className="text-muted-foreground">/</span>
								<input
									aria-label={`Команда ${index + 1}`}
									className="h-9 w-full bg-transparent text-sm outline-none"
									placeholder="menu"
									value={item.command}
									onChange={(event) =>
										setCommands(
											value.commands.map((command, i) =>
												i === index
													? { ...command, command: event.target.value.trim().toLowerCase() }
													: command,
											),
										)
									}
								/>
							</div>
							<Input
								aria-label={`Описание команды ${index + 1}`}
								placeholder="Описание для меню бота"
								maxLength={256}
								value={item.description ?? ""}
								onChange={(event) =>
									setCommands(
										value.commands.map((command, i) =>
											i === index
												? { ...command, description: event.target.value || undefined }
												: command,
										),
									)
								}
							/>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								aria-label={`Удалить команду ${index + 1}`}
								onClick={() => setCommands(value.commands.filter((_, i) => i !== index))}
							>
								<Trash2 />
							</Button>
							{item.command && !BOT_COMMAND_PATTERN.test(item.command) ? (
								<p className="col-span-3 text-xs text-red-400">
									Латиница в нижнем регистре, цифры и _
								</p>
							) : null}
						</div>
					))}
					<p className="text-xs text-muted-foreground">
						Команды с описанием попадут в меню бота после публикации.
					</p>
					{value.commands.length < MAX_TRIGGER_COMMANDS ? (
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() => setCommands([...value.commands, { command: "" }])}
						>
							<Plus />
							Команда
						</Button>
					) : null}
				</div>

				<div className="space-y-2">
					<p className="text-sm font-medium">Ключевые слова</p>
					{value.keywords.map((item, index) => (
						<div key={index} className="grid grid-cols-[9rem_1fr_auto] items-center gap-1">
							<NativeSelect
								aria-label={`Как сравнивать слово ${index + 1}`}
								value={item.match}
								onChange={(event) =>
									setKeywords(
										value.keywords.map((keyword, i) =>
											i === index
												? { ...keyword, match: event.target.value as KeywordMatch }
												: keyword,
										),
									)
								}
							>
								{KEYWORD_MATCHES.map((match) => (
									<option key={match} value={match}>
										{MATCH_LABELS[match]}
									</option>
								))}
							</NativeSelect>
							<Input
								aria-label={`Ключевое слово ${index + 1}`}
								placeholder="цена"
								maxLength={64}
								value={item.text}
								onChange={(event) =>
									setKeywords(
										value.keywords.map((keyword, i) =>
											i === index ? { ...keyword, text: event.target.value } : keyword,
										),
									)
								}
							/>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								aria-label={`Удалить слово ${index + 1}`}
								onClick={() => setKeywords(value.keywords.filter((_, i) => i !== index))}
							>
								<Trash2 />
							</Button>
						</div>
					))}
					{value.keywords.length < MAX_TRIGGER_KEYWORDS ? (
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() => setKeywords([...value.keywords, { text: "", match: "contains" }])}
						>
							<Plus />
							Слово
						</Button>
					) : null}
				</div>

				<div className="space-y-2">
					<p className="text-sm font-medium">События</p>
					{value.events.map((item, index) => (
						<div key={index} className="grid grid-cols-[1fr_auto] items-center gap-1">
							<Input
								aria-label={`Событие ${index + 1}`}
								placeholder="channel_joined"
								maxLength={64}
								value={item.code}
								onChange={(event) =>
									setEvents(
										value.events.map((current, i) =>
											i === index ? { code: event.target.value.trim().toLowerCase() } : current,
										),
									)
								}
							/>
							<Button
								type="button"
								variant="ghost"
								size="icon"
								aria-label={`Удалить событие ${index + 1}`}
								onClick={() => setEvents(value.events.filter((_, i) => i !== index))}
							>
								<Trash2 />
							</Button>
							{item.code && !EVENT_CODE_PATTERN.test(item.code) ? (
								<p className="col-span-2 text-xs text-red-400">
									Латиница в нижнем регистре, цифры и _
								</p>
							) : null}
						</div>
					))}
					<p className="text-xs text-muted-foreground">
						Событие присылает другая система проекта. channel_joined: человек вступил в канал
						клуба.
					</p>
					{value.events.length < MAX_TRIGGER_EVENTS ? (
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() => setEvents([...value.events, { code: "" }])}
						>
							<Plus />
							Событие
						</Button>
					) : null}
				</div>

				{flow?.kind === "CUSTOM" ? (
					<Label className="flex items-start gap-2 rounded-lg border border-border/60 p-3 text-sm font-normal">
						<Checkbox
							className="mt-0.5"
							checked={value.background}
							onCheckedChange={(checked) => setValue({ ...value, background: checked === true })}
						/>
						<span>
							<span className="font-medium">Идёт в фоне</span>
							<span className="block text-xs text-muted-foreground">
								Идёт параллельно диалогу: паузы не сбрасываются, когда человек жмёт меню или уходит
								в другую воронку, и сама воронка диалог не прерывает. Ждать ответа в нём нельзя, можно
								только сообщения, кнопки-ссылки, паузы, метки и условия.
							</span>
						</span>
					</Label>
				) : null}

				{error ? <p className="text-sm text-red-400">{error}</p> : null}
				<DialogFooter>
					<Button type="button" disabled={!!error || saving} onClick={() => void save()}>
						Сохранить
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
